import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { AgentCard } from './AgentCard';
import { TaskBoard } from './TaskBoard';
import { CrewBuilder } from './CrewBuilder';
import { ExecutionMonitor } from './ExecutionMonitor';
import { Bot, Users, CheckCircle, Activity, Plus, LogOut, User } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useAgents } from '../hooks/useAgents';
import { useTasks } from '../hooks/useTasks';
import { useCrews } from '../hooks/useCrews';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Dashboard3DBackground } from './ui/Dashboard3DBackground';
import { ThemeToggle } from './ui/theme-toggle';

// Mock data for executions and logs since we don't have AI integration yet
interface TaskExecution {
  id: string;
  taskId: string;
  agentId: string;
  status: 'running' | 'completed' | 'error';
  startTime: Date;
  endTime?: Date;
  result?: string;
  error?: string;
}

interface ExecutionLog {
  id: string;
  taskExecutionId?: string;
  agentId?: string;
  taskId?: string;
  timestamp: Date;
  level: 'info' | 'warning' | 'error';
  message: string;
}

export function AgentDashboard() {
  const { user, signOut } = useAuth();
  const { agents, loading: agentsLoading, createAgent, updateAgent } = useAgents();
  const { tasks, loading: tasksLoading, createTask, updateTask } = useTasks();
  const { crews, loading: crewsLoading, createCrew, startCrew } = useCrews();
  const { toast } = useToast();

  // Mock executions and logs for demonstration
  const [executions] = useState<TaskExecution[]>([]);
  const [logs, setLogs] = useState<ExecutionLog[]>([
    {
      id: '1',
      timestamp: new Date(),
      level: 'info',
      message: 'AI Agent Hub initialized successfully',
      agentId: agents[0]?.id
    },
    {
      id: '2',
      timestamp: new Date(Date.now() - 30000),
      level: 'info',
      message: 'Database connection established',
    },
    {
      id: '3',
      timestamp: new Date(Date.now() - 60000),
      level: 'info',
      message: 'Authentication system ready',
    }
  ]);

  // Agent creation dialog state
  const [isAgentDialogOpen, setIsAgentDialogOpen] = useState(false);
  const [newAgent, setNewAgent] = useState<{
    name: string;
    role: string;
    description: string;
    backstory: string;
    model: 'gpt-4' | 'claude-3' | 'gemini-pro' | 'gpt-5';
    temperature: number;
    skills: string[];
    status: 'idle' | 'working' | 'completed' | 'error';
  }>({
    name: '',
    role: '',
    description: '',
    backstory: '',
    model: 'gpt-4' as const,
    temperature: 0.7,
    skills: [] as string[],
    status: 'idle' as const
  });
  const [newSkill, setNewSkill] = useState('');

  // Task creation dialog state
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  const [newTask, setNewTask] = useState<{
    title: string;
    description: string;
    priority: 'low' | 'medium' | 'high' | 'urgent';
    estimated_time: number;
    status: 'pending' | 'in-progress' | 'completed' | 'failed';
  }>({
    title: '',
    description: '',
    priority: 'medium' as const,
    estimated_time: 60,
    status: 'pending' as const
  });

  // Simulate real-time log updates
  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() > 0.7) { // 30% chance every 5 seconds
        const messages = [
          'System monitoring active',
          'Agent performance metrics updated',
          'Task queue processed',
          'Database optimization completed',
          'Security scan completed successfully'
        ];
        const levels: ('info' | 'warning' | 'error')[] = ['info', 'info', 'info', 'warning'];
        
        const newLog: ExecutionLog = {
          id: `log_${Date.now()}`,
          timestamp: new Date(),
          level: levels[Math.floor(Math.random() * levels.length)],
          message: messages[Math.floor(Math.random() * messages.length)],
        };
        
        setLogs(prev => [newLog, ...prev.slice(0, 49)]);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleCreateAgent = async () => {
    try {
      await createAgent(newAgent);
      setIsAgentDialogOpen(false);
      setNewAgent({
        name: '',
        role: '',
        description: '',
        backstory: '',
        model: 'gpt-4',
        temperature: 0.7,
        skills: [],
        status: 'idle'
      });
      toast({
        title: "Success",
        description: "Agent created successfully!",
      });
    } catch (error) {
      console.error('Failed to create agent:', error);
    }
  };

  const handleCreateTask = async () => {
    try {
      await createTask(newTask);
      setIsTaskDialogOpen(false);
      setNewTask({
        title: '',
        description: '',
        priority: 'medium',
        estimated_time: 60,
        status: 'pending'
      });
      toast({
        title: "Success",
        description: "Task created successfully!",
      });
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  const handleStartAgent = async (agent: any) => {
    await updateAgent(agent.id, { status: 'working' });
    toast({
      title: "Agent Started",
      description: `${agent.name} is now active and working.`,
    });
  };

  const handleStopAgent = async (agent: any) => {
    await updateAgent(agent.id, { status: 'idle' });
    toast({
      title: "Agent Stopped", 
      description: `${agent.name} has been stopped.`,
    });
  };

  const handleTaskClick = (task: { id: string; title: string; description: string; priority: 'low' | 'medium' | 'high' | 'urgent'; status: 'pending' | 'in-progress' | 'completed' | 'failed'; estimated_time: number; assigned_agent_id?: string; }) => {
    console.log('Task clicked:', task);
  };

  const handleAssignAgent = (taskId: string, agentId: string) => {
    updateTask(taskId, { assigned_agent_id: agentId });
  };

  const addSkill = () => {
    if (newSkill.trim() && !newAgent.skills.includes(newSkill.trim())) {
      setNewAgent(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const removeSkill = (skillToRemove: string) => {
    setNewAgent(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  const getStats = () => {
    const activeAgents = agents.filter(agent => agent.status === 'working').length;
    const completedTasks = tasks.filter(task => task.status === 'completed').length;
    const runningCrews = crews.filter(crew => crew.status === 'running').length;
    
    return { activeAgents, completedTasks, runningCrews, totalAgents: agents.length };
  };

  const handleSignOut = async () => {
    await signOut();
    toast({
      title: "Signed Out",
      description: "You have been successfully signed out.",
    });
  };

  const stats = getStats();
  const loading = agentsLoading || tasksLoading || crewsLoading;

  const handleCreateCrewSimple = async (crew: { name: string; description: string; agent_ids: string[]; task_ids: string[] }) => {
    await createCrew({
      name: crew.name,
      description: crew.description,
      agent_ids: crew.agent_ids,
      task_ids: crew.task_ids,
      // other fields handled in hook implementation
    } as any);
  };

  const handleCreateAgentSimple = async (agent: { name: string; role: string; skills: string[] }) => {
    await createAgent({
      name: agent.name,
      role: agent.role,
      description: '',
      backstory: '',
      model: 'gpt-4',
      temperature: 0.7,
      skills: agent.skills,
      status: 'idle',
    } as any);
  };

  const handleCreateTaskSimple = async (task: { title: string; description: string }) => {
    await createTask({
      title: task.title,
      description: task.description,
      priority: 'medium',
      status: 'pending',
      estimated_time: 60,
    } as any);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/50 to-primary/10 relative">
      {/* 3D Background */}
      <Dashboard3DBackground 
        showParticles={true}
        showAgents={true}
        showTaskFlow={true}
        showMetrics={true}
        intensity="medium"
      />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1517336714731-489689fd1ca8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1974&q=80)` }}
        />
        <div className="relative bg-gradient-to-r from-primary/20 via-transparent to-secondary/20 backdrop-blur-sm">
          <div className="container mx-auto px-4 py-12">
            <div className="flex justify-between items-start mb-8">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-full bg-primary/20">
                    <Bot className="h-8 w-8 text-primary" />
                  </div>
                  <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                    AI Agent Hub
                  </h1>
                </div>
                <p className="text-lg text-muted-foreground max-w-2xl">
                  Orchestrate intelligent AI agents, manage complex tasks, and build powerful crews. 
                  Your command center for autonomous AI workflows.
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <User className="h-4 w-4" />
                  {user?.email}
                </div>
                <ThemeToggle />
                <Button variant="outline" onClick={handleSignOut} className="gap-2">
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </Button>
              </div>
            </div>
            
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="border-0 shadow-lg bg-card/80 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-blue-500/20">
                      <Bot className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Active Agents</p>
                      <p className="text-2xl font-bold">{stats.activeAgents}/{stats.totalAgents}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg bg-card/80 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-green-500/20">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Tasks Completed</p>
                      <p className="text-2xl font-bold">{stats.completedTasks}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg bg-card/80 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-purple-500/20">
                      <Users className="h-5 w-5 text-purple-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Active Crews</p>
                      <p className="text-2xl font-bold">{stats.runningCrews}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg bg-card/80 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-orange-500/20">
                      <Activity className="h-5 w-5 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">System Status</p>
                      <Badge variant="default" className="bg-green-500/20 text-green-700 hover:bg-green-500/30">
                        Operational
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 lg:w-fit lg:grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="agents">Agents</TabsTrigger>
            <TabsTrigger value="tasks">Tasks</TabsTrigger>
            <TabsTrigger value="crews">Crews</TabsTrigger>
            <TabsTrigger value="monitor">Monitor</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Activity */}
              <Card className="border-0 shadow-lg bg-card/80 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-64">
                    <div className="space-y-3">
                      {logs.slice(0, 10).map((log) => (
                        <div key={log.id} className="flex items-start gap-3 text-sm">
                          <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                            log.level === 'error' ? 'bg-red-500' : 
                            log.level === 'warning' ? 'bg-yellow-500' : 'bg-green-500'
                          }`} />
                          <div className="flex-1">
                            <p className="text-foreground">{log.message}</p>
                            <p className="text-xs text-muted-foreground">
                              {log.timestamp.toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Agent Performance */}
              <Card className="border-0 shadow-lg bg-card/80 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bot className="h-5 w-5" />
                    Agent Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-64">
                    <div className="space-y-4">
                      {agents.slice(0, 5).map((agent) => (
                        <div key={agent.id} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${
                              agent.status === 'working' ? 'bg-green-500' :
                              agent.status === 'completed' ? 'bg-blue-500' : 
                              agent.status === 'error' ? 'bg-red-500' : 'bg-gray-500'
                            }`} />
                            <div>
                              <p className="font-medium">{agent.name}</p>
                              <p className="text-xs text-muted-foreground">{agent.role}</p>
                            </div>
                          </div>
                          <Badge variant={agent.status === 'working' ? 'default' : 'secondary'}>
                            {agent.status}
                          </Badge>
                        </div>
                      ))}
                      {agents.length === 0 && !loading && (
                        <p className="text-muted-foreground text-center py-8">
                          No agents created yet. Create your first agent in the Agents tab.
                        </p>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="agents" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">AI Agents</h2>
                <p className="text-muted-foreground">Create and manage your intelligent agents</p>
              </div>
              <Dialog open={isAgentDialogOpen} onOpenChange={setIsAgentDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2">
                    <Plus className="h-4 w-4" />
                    Create Agent
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Create New Agent</DialogTitle>
                    <DialogDescription>
                      Configure your AI agent with specific skills and personality
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="agent-name">Name</Label>
                      <Input
                        id="agent-name"
                        value={newAgent.name}
                        onChange={(e) => setNewAgent(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Research Analyst"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="agent-role">Role</Label>
                      <Input
                        id="agent-role"
                        value={newAgent.role}
                        onChange={(e) => setNewAgent(prev => ({ ...prev, role: e.target.value }))}
                        placeholder="e.g., Data Researcher"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="agent-description">Description</Label>
                      <Textarea
                        id="agent-description"
                        value={newAgent.description}
                        onChange={(e) => setNewAgent(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="What does this agent do?"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="agent-backstory">Backstory</Label>
                      <Textarea
                        id="agent-backstory"
                        value={newAgent.backstory}
                        onChange={(e) => setNewAgent(prev => ({ ...prev, backstory: e.target.value }))}
                        placeholder="Agent's background and expertise"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="agent-model">AI Model</Label>
                      <Select 
                        value={newAgent.model} 
                        onValueChange={(value: 'gpt-4' | 'claude-3' | 'gemini-pro' | 'gpt-5') => 
                          setNewAgent(prev => ({ ...prev, model: value }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="gpt-4">GPT-4</SelectItem>
                          <SelectItem value="claude-3">Claude-3</SelectItem>
                          <SelectItem value="gemini-pro">Gemini Pro</SelectItem>
                          <SelectItem value="gpt-5">GPT-5</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="agent-skills">Skills</Label>
                      <div className="flex gap-2">
                        <Input
                          value={newSkill}
                          onChange={(e) => setNewSkill(e.target.value)}
                          placeholder="Add a skill"
                          onKeyPress={(e) => e.key === 'Enter' && addSkill()}
                        />
                        <Button type="button" onClick={addSkill} variant="outline">Add</Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {newAgent.skills.map((skill) => (
                          <Badge key={skill} variant="secondary" className="cursor-pointer" onClick={() => removeSkill(skill)}>
                            {skill} ×
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <Button onClick={handleCreateAgent} className="w-full">
                      Create Agent
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {agents.map((agent) => (
                <AgentCard 
                  key={agent.id} 
                  agent={agent}
                  onStart={() => handleStartAgent(agent)}
                  onStop={() => handleStopAgent(agent)}
                />
              ))}
              {agents.length === 0 && !loading && (
                <Card className="col-span-full border-2 border-dashed border-muted-foreground/25 bg-card/50">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Bot className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No agents yet</h3>
                    <p className="text-muted-foreground mb-4 text-center">
                      Create your first AI agent to get started with intelligent automation
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="tasks" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">Task Management</h2>
                <p className="text-muted-foreground">Organize and track your AI-powered tasks</p>
              </div>
              <Dialog open={isTaskDialogOpen} onOpenChange={setIsTaskDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2">
                    <Plus className="h-4 w-4" />
                    Create Task
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Create New Task</DialogTitle>
                    <DialogDescription>
                      Define a task for your AI agents to complete
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="task-title">Title</Label>
                      <Input
                        id="task-title"
                        value={newTask.title}
                        onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="e.g., Research market trends"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="task-description">Description</Label>
                      <Textarea
                        id="task-description"
                        value={newTask.description}
                        onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Detailed task description..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="task-priority">Priority</Label>
                      <Select 
                        value={newTask.priority} 
                        onValueChange={(value: 'low' | 'medium' | 'high' | 'urgent') => 
                          setNewTask(prev => ({ ...prev, priority: value }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="task-time">Estimated Time (minutes)</Label>
                      <Input
                        id="task-time"
                        type="number"
                        value={newTask.estimated_time}
                        onChange={(e) => setNewTask(prev => ({ ...prev, estimated_time: parseInt(e.target.value) || 60 }))}
                      />
                    </div>
                    <Button onClick={handleCreateTask} className="w-full">
                      Create Task
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            
            <TaskBoard 
              tasks={tasks}
              agents={agents}
              onTaskClick={handleTaskClick}
              onAssignAgent={handleAssignAgent}
            />
          </TabsContent>

          <TabsContent value="crews" className="space-y-6">
            <CrewBuilder
              crews={crews as any}
              agents={agents as any}
              onCreateCrew={handleCreateCrewSimple}
              onCreateAgent={handleCreateAgentSimple}
              onCreateTask={handleCreateTaskSimple}
              onStartCrew={startCrew}
            />
          </TabsContent>

          <TabsContent value="monitor" className="space-y-6">
            <ExecutionMonitor
              executions={executions}
              logs={logs}
              agents={agents}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}